
import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataCache } from '../../context/DataCacheContext';
import { useAppDataRefresh } from '../../hooks/useAppDataRefresh';
import EmptyState from '../common/EmptyState';
import { STATUS_LABELS, getStatusClass, MANUAL_STATUSES } from './EditEventModal';
import './TrainingEvents.css';

// Lazy loading для модального окна создания
const CreateEventModal = lazy(() => import('./CreateEventModal'));

// SVG иконки
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
    <path d="M21 3v6h-6" />
  </svg>
);

const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ParticipantsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const TrainingEvents = () => {
  const navigate = useNavigate();
  const { trainingEvents, fetchTrainingEvents } = useDataCache();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [formatFilter, setFormatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Автоматическое обновление при глобальных изменениях
  const handleDataRefresh = useCallback(async (eventData) => {
    console.log('TrainingEvents: обнаружены изменения данных:', eventData);
    await fetchTrainingEvents({ force: true });
  }, [fetchTrainingEvents]);

  useAppDataRefresh(handleDataRefresh);

  // Загрузка мероприятий при монтировании, если нет данных
  useEffect(() => {
    if (trainingEvents.data.length === 0 && trainingEvents.status !== 'loading') {
      fetchTrainingEvents();
    }
  }, [trainingEvents.data.length, trainingEvents.status, fetchTrainingEvents]);

  // Принудительное обновление
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchTrainingEvents({ force: true });
    setIsRefreshing(false);
  };

  // Фильтрация мероприятий с мемоизацией
  const filteredEvents = useMemo(() => {
    let filtered = trainingEvents.data;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.title?.toLowerCase().includes(term) ||
        (event.training_center && event.training_center.toLowerCase().includes(term)) ||
        (event.location && event.location.toLowerCase().includes(term))
      );
    }

    if (formatFilter) {
      filtered = filtered.filter(event => event.format === formatFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    return filtered;
  }, [trainingEvents.data, searchTerm, formatFilter, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '—';
    if (startDate === endDate) return formatDate(startDate);
    return `${formatDate(startDate)} — ${formatDate(endDate)}`;
  };

  const getFormatText = (format) => {
    switch (format) {
      case 'online': return 'Онлайн';
      case 'onsite': return 'Оффлайн';
      case 'hybrid': return 'Гибрид';
      default: return format || '—';
    }
  };

  const getFormatClass = (format) => {
    switch (format) {
      case 'online': return 'format-online';
      case 'onsite': return 'format-offline';
      case 'hybrid': return 'format-hybrid';
      default: return '';
    }
  };

  // Получение текста статуса с использованием STATUS_LABELS
  const getStatusText = (status) => {
    return STATUS_LABELS[status] || status || '—';
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFormatFilter('');
    setStatusFilter('');
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleOpenViewPage = (event) => {
    navigate(`/training-events/${event.id}`);
  };

  const handleEventCreated = () => {
    setIsCreateModalOpen(false);
    fetchTrainingEvents({ force: true });
  };

  const hasActiveFilters = searchTerm || formatFilter || statusFilter;

  // Состояние загрузки
  if (trainingEvents.status === 'loading' && trainingEvents.data.length === 0) {
    return (
      <div className="events-container">
        <div className="events-header">
          <h2>Мероприятия</h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="skeleton-button" style={{ width: '180px', height: '36px', borderRadius: '8px', backgroundColor: '#e5e7eb' }}></div>
            <div className="skeleton-button" style={{ width: '100px', height: '36px', borderRadius: '8px', backgroundColor: '#e5e7eb' }}></div>
          </div>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка мероприятий...</p>
        </div>
      </div>
    );
  }

  // Состояние ошибки
  if (trainingEvents.status === 'error' && trainingEvents.data.length === 0) {
    return (
      <div className="events-container">
        <div className="events-header">
          <h2>Мероприятия</h2>
        </div>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Ошибка загрузки</h3>
          <p>{trainingEvents.error || 'Не удалось загрузить мероприятия'}</p>
          <button className="btn-primary" onClick={() => fetchTrainingEvents({ force: true })}>
            Повторить попытку
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="events-container">
      <div className="events-header">
        <h2>Мероприятия</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-primary" onClick={handleOpenCreateModal}>
            <span style={{ marginRight: '6px' }}><PlusIcon /></span>
            Создать мероприятие
          </button>
          <button 
            className="btn-secondary" 
            onClick={handleRefresh} 
            disabled={trainingEvents.status === 'loading' || isRefreshing}
          >
            <span style={{ marginRight: '6px' }}>
              <RefreshIcon />
            </span>
            {isRefreshing ? 'Обновление...' : 'Обновить'}
          </button>
        </div>
      </div>

      <div className="events-filters">
        <input
          type="text"
          className="search-input"
          placeholder="Поиск по названию, центру или локации..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={formatFilter}
          onChange={(e) => setFormatFilter(e.target.value)}
        >
          <option value="">Все форматы</option>
          <option value="online">Онлайн</option>
          <option value="onsite">Оффлайн</option>
          <option value="hybrid">Гибрид</option>
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Все статусы</option>
          {MANUAL_STATUSES.map(status => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
          
        </select>
        {hasActiveFilters && (
          <button className="btn-secondary btn-sm" onClick={resetFilters} title="Сбросить фильтры">
            <span style={{ marginRight: '4px' }}><XIcon /></span>
            Сбросить
          </button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="filter-stats">
          <span className="filter-stats-text">
            Найдено: {filteredEvents.length} из {trainingEvents.data.length} мероприятий
          </span>
        </div>
      )}

      {filteredEvents.length === 0 ? (
        <EmptyState
          icon="📅"
          title={hasActiveFilters ? "Мероприятия не найдены" : "Нет мероприятий"}
          subtitle={hasActiveFilters ? "Измените параметры фильтрации" : "Мероприятия пока не запланированы"}
        />
      ) : (
        <div className="events-grid">
          {filteredEvents.map(event => (
            <div 
              key={event.id} 
              className="event-card"
              onClick={() => handleOpenViewPage(event)}
              style={{ cursor: 'pointer' }}
            >
              <div className="event-card-header">
                <h3 className="event-title" title={event.title}>{event.title}</h3>
                <div className="event-badges-row">
                  <div className="event-badges">
                    <span className={`format-badge ${getFormatClass(event.format)}`}>
                      {getFormatText(event.format)}
                    </span>
                    <span className={`status-badge ${getStatusClass(event.status)}`}>
                      {getStatusText(event.status)}
                    </span>
                  </div>
                  <div className="event-participants-badge">
                    <span className="participants-icon"><ParticipantsIcon /></span>
                    <span className="participants-count-badge">
                      {event.participants_count || 0}
                    </span>
                    {event.max_participants > 0 && (
                      <span className="participants-max-badge">
                        /{event.max_participants}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="event-card-body">
                <div className="event-info-row">
                  <span className="event-label">Даты:</span>
                  <span className="event-value">
                    {formatDateRange(event.start_date, event.end_date)}
                  </span>
                </div>

                <div className="event-info-row">
                  <span className="event-label">Адрес:</span>
                  <span className="event-value">
                    {event.location || 'Не указан'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lazy loaded модальное окно создания */}
      {isCreateModalOpen && (
        <Suspense fallback={<div className="modal-overlay"><div className="loading-state"><div className="spinner"></div></div></div>}>
          <CreateEventModal
            isOpen={isCreateModalOpen}
            onClose={handleCloseCreateModal}
            onSuccess={handleEventCreated}
          />
        </Suspense>
      )}
    </div>
  );
};

export default TrainingEvents;